import { useMemo, useState } from "react";
import { ListPageContainer } from "@/components/shared/ListPageContainer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  FileText,
  Trash2,
  Edit,
  History,
  Save,
  User,
  Download,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { getStatusLabel } from "@/config/status-registry";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useConfirm } from "@/providers/ConfirmProvider";
import { DataTableRow, DataTableRowCheckbox, DataTableSelectAllCheckbox } from "@/components/shared/DataTableRow";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { exportToExcel } from "@/lib/excel.utils";

import { toVnDateString } from "@apv-erp/status";
const INITIAL_REQUESTS = [
  {
    id: "REQ-2024-001",
    type: "Xin nghỉ phép",
    date: "2024-01-15",
    status: "approved",
    description: "Nghỉ phép năm (2 ngày)",
    logs: [
      { time: "2024-01-15 08:30", action: "Tạo yêu cầu", user: "Nguyễn Văn A" },
      { time: "2024-01-15 10:00", action: "Đã duyệt", user: "Trần Quản Lý" },
    ],
  },
  {
    id: "REQ-2024-002",
    type: "Thanh toán",
    date: "2024-01-20",
    status: "pending",
    description: "Chi phí đi lại công tác đi Hà Nội",
    logs: [
      { time: "2024-01-20 09:00", action: "Tạo yêu cầu", user: "Nguyễn Văn A" },
    ],
  },
  {
    id: "REQ-2024-003",
    type: "Đề xuất thiết bị",
    date: "2024-02-01",
    status: "rejected",
    description: "Xin cấp chuột máy tính mới",
    logs: [
      { time: "2024-02-01 14:00", action: "Tạo yêu cầu", user: "Nguyễn Văn A" },
      {
        time: "2024-02-02 08:00",
        action: "Từ chối: Kho còn hàng cũ",
        user: "Lê Admin",
      },
    ],
  },
  {
    id: "REQ-2024-004",
    type: "Xin nghỉ phép",
    date: "2024-02-10",
    status: "pending",
    description: "Nghỉ ốm (1 ngày)",
    logs: [
      { time: "2024-02-10 07:45", action: "Tạo yêu cầu", user: "Nguyễn Văn A" },
    ],
  },
];

export default function MyRequestsPage() {
  const confirm = useConfirm();
  const [requests, setRequests] = useState(INITIAL_REQUESTS);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"view" | "edit" | "create">("view");
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const rowIds = useMemo(() => requests.map((r) => r.id), [requests]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;
  const exportRequests = (rows: any[]) =>
    exportToExcel(
      rows.map((r) => ({ ...r, statusLabel: getStatusLabel("PurchaseRequestStatus", r.status) || r.status })),
      [
        { key: "id", label: "Mã phiếu", width: 16 },
        { key: "type", label: "Loại yêu cầu", width: 20 },
        { key: "description", label: "Nội dung / Lý do", width: 40 },
        { key: "date", label: "Ngày gửi", width: 14 },
        { key: "statusLabel", label: "Trạng thái", width: 16 },
      ],
      "yeu_cau_cua_toi",
    );

  // Form State
  const [formData, setFormData] = useState({
    type: "",
    description: "",
    date: toVnDateString(),
  });

  const handleCreate = () => {
    setSelectedRequest(null);
    setFormData({
      type: "Xin nghỉ phép",
      description: "",
      date: toVnDateString(),
    });
    setViewMode("create");
    setIsSheetOpen(true);
  };

  const handleView = (req: any) => {
    setSelectedRequest(req);
    setViewMode("view");
    setIsSheetOpen(true);
  };

  const handleEdit = () => {
    if (!selectedRequest) return;
    setFormData({
      type: selectedRequest.type,
      description: selectedRequest.description,
      date: selectedRequest.date,
    });
    setViewMode("edit");
  };

  const handleSave = () => {
    if (!formData.description) {
      toast.error("Vui lòng nhập nội dung yêu cầu");
      return;
    }

    const timestamp = new Date().toLocaleString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const newLog = {
      time: timestamp,
      action: viewMode === "create" ? "Tạo yêu cầu" : "Cập nhật thông tin",
      user: "Nguyễn Văn A",
    };

    if (viewMode === "edit" && selectedRequest) {
      // Update
      const updatedReq = {
        ...selectedRequest,
        ...formData,
        logs: [newLog, ...(selectedRequest.logs || [])],
      };
      setRequests(
        requests.map((r) => (r.id === selectedRequest.id ? updatedReq : r)),
      );
      setSelectedRequest(updatedReq);

      setViewMode("view");
    } else {
      // Create
      const newId = `REQ-2024-${(requests.length + 1).toString().padStart(3, "0")}`;
      const newReq = {
        id: newId,
        status: "pending",
        ...formData,
        logs: [newLog],
      };
      setRequests([newReq, ...requests]);

      setIsSheetOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRequest) return;
    if (await confirm("Bạn có chắc muốn xóa yêu cầu này?")) {
      setRequests(requests.filter((r) => r.id !== selectedRequest.id));

      setIsSheetOpen(false);
    }
  };

  return (
    <ListPageContainer
      actions={
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            onClick={() => exportRequests(requests)}
            className="shadow-sm h-8 text-xs"
          >
            <Download className="mr-1.5 h-3.5 w-3.5" /> Xuất Excel
          </Button>
          <Button
            onClick={handleCreate}
            className="bg-indigo-600 hover:bg-indigo-700 shadow-sm h-8 text-xs"
          >
            <Plus className="mr-2 h-3.5 w-3.5" /> Tạo yêu cầu
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200 shadow-sm p-4 flex flex-col items-center justify-center bg-white hover:border-indigo-200 transition-colors">
            <span className="text-3xl font-bold text-slate-900">12</span>
            <span className="text-xs text-slate-500 font-bold uppercase mt-1">
              Ngày phép còn lại
            </span>
          </Card>
          <Card className="border-slate-200 shadow-sm p-4 flex flex-col items-center justify-center bg-white hover:border-indigo-200 transition-colors">
            <span className="text-3xl font-bold text-amber-600">
              {requests.filter((r) => r.status === "pending").length}
            </span>
            <span className="text-xs text-slate-500 font-bold uppercase mt-1">
              Đang chờ duyệt
            </span>
          </Card>
          <Card className="border-slate-200 shadow-sm p-4 flex flex-col items-center justify-center bg-white hover:border-indigo-200 transition-colors">
            <span className="text-3xl font-bold text-indigo-600">
              {requests.length}
            </span>
            <span className="text-xs text-slate-500 font-bold uppercase mt-1">
              Tổng yêu cầu
            </span>
          </Card>
          <Card className="border-slate-200 shadow-sm p-4 flex flex-col items-center justify-center bg-indigo-50 border-indigo-100 cursor-pointer hover:bg-indigo-100 transition-colors">
            <FileText className="w-8 h-8 text-indigo-500 mb-2" />
            <span className="text-xs text-indigo-700 font-bold uppercase">
              Quy định công ty
            </span>
          </Card>
        </div>

        <Card className="border-slate-200 shadow-sm overflow-hidden md:overflow-visible bg-white erp-table-wrapper">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                <TableHead className="w-[120px] font-bold">Mã Phiếu</TableHead>
                <TableHead className="font-bold">Loại yêu cầu</TableHead>
                <TableHead className="font-bold">Nội dung / Lý do</TableHead>
                <TableHead className="font-bold text-center">
                  Ngày gửi
                </TableHead>
                <TableHead className="text-center font-bold">
                  Trạng thái
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((req) => (
                <DataTableRow
                  key={req.id}
                  id={req.id}
                  rowSelection={rowSelection}
                  className="hover:bg-slate-50 group"
                  onClick={() => handleView(req)}
                >
                  <DataTableRowCheckbox id={req.id} rowSelection={rowSelection} />
                  <TableCell className="font-mono text-xs font-bold text-slate-500">
                    {req.id}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    {req.type}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm max-w-[300px] truncate">
                    {req.description}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm text-center">
                    {req.date}
                  </TableCell>
                  <TableCell className="flex justify-center">
                    <StatusBadge
                      type="PurchaseRequestStatus"
                      value={req.status}
                    />
                  </TableCell>
                </DataTableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={[
          {
            icon: <Download className="h-4 w-4" />,
            label: `Xuất Excel ${selectedCount} dòng`,
            onClick: () => exportRequests(requests.filter((r) => selectedIds.has(r.id))),
          },
        ]}
      />

      <Dialog open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <DialogContent className="w-[400px] sm:w-[540px] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {viewMode === "create" && "Tạo yêu cầu mới"}
              {viewMode === "edit" && "Chỉnh sửa yêu cầu"}
              {viewMode === "view" &&
                selectedRequest &&
                `Chi tiết ${selectedRequest.id}`}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {viewMode === "view"
                ? "Xem thông tin chi tiết và lịch sử xử lý"
                : "Điền thông tin cho yêu cầu của bạn"}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 py-4 overflow-hidden flex flex-col gap-4">
            {/* VIEW MODE */}
            {viewMode === "view" && selectedRequest && (
              <div className="flex flex-col h-full">
                <div className="space-y-4">
                  <div className="flex justify-between items-start p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div>
                      <div className="text-xs text-slate-500 font-medium uppercase mb-1">
                        Trạng thái hiện tại
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge
                          type="PurchaseRequestStatus"
                          value={selectedRequest.status}
                        />
                        <span className="text-sm font-semibold text-slate-700">
                          {getStatusLabel(
                            "ApprovalStatus",
                            selectedRequest.status,
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500 font-medium uppercase mb-1">
                        Ngày gửi
                      </div>
                      <div className="font-mono font-bold text-slate-800">
                        {selectedRequest.date}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-sm font-medium text-slate-500">
                      Loại yêu cầu:
                    </div>
                    <div className="col-span-2 font-medium text-slate-900">
                      {selectedRequest.type}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-sm font-medium text-slate-500">
                      Nội dung chi tiết:
                    </div>
                    <div className="col-span-2 text-sm text-slate-700 leading-relaxed bg-white p-2 rounded border border-slate-100 min-h-[80px]">
                      {selectedRequest.description}
                    </div>
                  </div>

                  <Separator className="my-4" />
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
                    <History className="w-4 h-4 text-indigo-500" />
                    Nhật ký xử lý
                  </div>
                </div>

                <ScrollArea className="flex-1 mt-2 -mr-4 pr-4">
                  <div className="relative pl-4 border-l border-slate-200 ml-2 space-y-6 py-2">
                    {selectedRequest.logs &&
                      selectedRequest.logs.map((log: any, index: number) => (
                        <div key={index} className="relative">
                          <div className="absolute -left-[21px] top-0 w-2.5 h-2.5 rounded-full bg-slate-300 border-2 border-white ring-1 ring-slate-100"></div>
                          <div className="text-xs font-mono text-slate-400 mb-0.5">
                            {log.time}
                          </div>
                          <div className="font-medium text-sm text-slate-800">
                            {log.action}
                          </div>
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <User className="w-3 h-3" /> {log.user}
                          </div>
                        </div>
                      ))}
                    {(!selectedRequest.logs ||
                      selectedRequest.logs.length === 0) && (
                      <div className="text-sm text-slate-400 italic">
                        Chưa có lịch sử ghi nhận
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* EDIT/CREATE MODE */}
            {(viewMode === "edit" || viewMode === "create") && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Loại yêu cầu</Label>
                  <ComboboxConfigMaster
                    categoryCode="request_types"
                    value={formData.type}
                    onChange={(val) => setFormData({ ...formData, type: val })}
                    placeholder="Chọn hoặc nhập loại đề xuất..."
                    hideLabel
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Ngày</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Nội dung chi tiết</Label>
                  <Textarea
                    id="description"
                    rows={5}
                    placeholder="Nhập lý do hoặc chi tiết yêu cầu..."
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            {viewMode === "view" ? (
              <div className="flex w-full justify-between">
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  className="bg-rose-50 text-rose-600 hover:bg-rose-100 border-none shadow-none"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Xóa yêu cầu
                </Button>
                <Button
                  onClick={handleEdit}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Edit className="w-4 h-4 mr-2" /> Chỉnh sửa
                </Button>
              </div>
            ) : (
              <div className="flex w-full justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    viewMode === "edit"
                      ? setViewMode("view")
                      : setIsSheetOpen(false)
                  }
                >
                  Hủy
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Save className="w-4 h-4 mr-2" /> Lưu thông tin
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ListPageContainer>
  );
}
